import type {AnyQueryRoute} from '@github-ui/react-core/future/query-route'
import {useRouteQueryKey} from '@github-ui/react-core/future/use-route-query-key'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {keepPreviousData, useQuery} from '@github-ui/react-query'
import {useLocation} from '@github-ui/react-router'
import {useState} from 'react'

import {canonicalQueryKey} from '../helpers/saved-view-route-cache'
import type {SavedViewRequestSearchParams} from './use-saved-view-prefetch'

interface RouteQueryForQuery<TData> {
  data: TData | undefined
  /** When `data` was fetched, including a previous query's response while it stands in. */
  dataUpdatedAt: number
  isFetching: boolean
  isError: boolean
  refetch: () => void
  queryKey: RouteQueryKey
}

/**
 * Fetches a route query as the route would for the URL that states `query`, so a query shown
 * without navigating, like an editor preview, shares the route's cache. The rest of the request
 * comes from the URL on screen. While the next response loads, the previous one stands in.
 */
export function useRouteQueryForQuery<TData>(
  route: AnyQueryRoute,
  queryName: string,
  {
    query,
    pathname,
    readSearchParams,
    enabled,
  }: {
    query: string
    /** The pathname the route requests this query from. */
    pathname: string
    /** Reads the request the query sends for a URL, the same way its `queryDeps` does. */
    readSearchParams: SavedViewRequestSearchParams
    enabled: boolean
  },
): RouteQueryForQuery<TData> {
  const routeQueryKey = useRouteQueryKey(route, queryName)
  if (!routeQueryKey) throw new Error(`useRouteQueryForQuery needs the ${route.id} ${queryName} query`)

  const searchParams = new URLSearchParams(useLocation().search)
  searchParams.set('q', query)
  const queryKey = canonicalQueryKey(routeQueryKey, pathname, readSearchParams(searchParams))
  const result = useQuery({...route.buildQueryOptions(queryName, queryKey), enabled, placeholderData: keepPreviousData})
  // A stand-in keeps its own response's time. Every stand-in reporting the same time would key them
  // all alike, so a board drawn for one stand-in would be drawn again for the next, for the same query.
  const [shownUpdatedAt, setShownUpdatedAt] = useState(0)
  if (!result.isPlaceholderData && result.data !== undefined && result.dataUpdatedAt !== shownUpdatedAt) {
    setShownUpdatedAt(result.dataUpdatedAt)
  }

  return {
    // `buildQueryOptions` drops the type `select` gives the route data, which `useSuspenseRouteQuery` keeps.
    data: result.data as TData | undefined,
    dataUpdatedAt: result.isPlaceholderData ? shownUpdatedAt : result.dataUpdatedAt,
    isFetching: result.isFetching,
    isError: result.isError,
    refetch: () => void result.refetch(),
    queryKey,
  }
}
